import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getVoterAwarenessBlogs, getBlogCategories } from '../../data/voterAwarenessBlogs';
import Header from '../Common/Header';
import './BlogList.css';

const BlogList = () => {
  const { t, i18n } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // Get blogs and categories based on current language
  const voterAwarenessBlogs = useMemo(() => 
    getVoterAwarenessBlogs(i18n.language), 
    [i18n.language]
  );
  
  const blogCategories = useMemo(() => 
    getBlogCategories(i18n.language), 
    [i18n.language]
  );

  // Reset category to "All" when language changes
  useEffect(() => {
    setSelectedCategory(blogCategories[0]); // Always set to first category (All/सभी)
  }, [i18n.language, blogCategories]);

  const filteredBlogs = voterAwarenessBlogs.filter(blog => {
    const matchesCategory = selectedCategory === blogCategories[0] || blog.category === selectedCategory;
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          blog.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const featuredBlogs = voterAwarenessBlogs.filter(blog => blog.featured);

  const handleBlogClick = (slug) => {
    navigate(`/blog/${slug}`);
  };

  return (
    <div className="blog-page">
      <Header />

      {/* Hero Section */}
      <div className="blog-hero">
        <div className="blog-hero-content">
          <h1>
            <i className="fas fa-book-open"></i>
            {t('blogs.title')}
          </h1>
          <p>{t('blogs.subtitle')}</p>
          <div className="hero-stats">
            <div className="hero-stat">
              <span className="stat-number">{voterAwarenessBlogs.length}</span>
              <span className="stat-label">{t('blogs.articlesCount')}</span>
            </div>
            <div className="hero-stat">
              <span className="stat-number">7 {i18n.language === 'hi' ? 'करोड़+' : 'Cr+'}</span>
              <span className="stat-label">{t('blogs.votersCount')}</span>
            </div>
            <div className="hero-stat">
              <span className="stat-number">243</span>
              <span className="stat-label">{t('blogs.constituenciesCount')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="blog-container">
        {/* Search and Filter Section */}
        <div className="blog-controls">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder={t('blogs.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="category-filters">
            {blogCategories.map(category => (
              <button
                key={category}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Blogs Section */}
        {selectedCategory === blogCategories[0] && !searchTerm && (
          <div className="featured-section">
            <h2 className="section-title">
              <i className="fas fa-star"></i>
              {t('blogs.featuredArticles')}
            </h2>
            <div className="featured-grid">
              {featuredBlogs.map(blog => (
                <div
                  key={blog.id}
                  className="featured-card"
                  onClick={() => handleBlogClick(blog.slug)}
                >
                  <div className="featured-image">
                    <img src={blog.image} alt={blog.title} />
                    <span className="featured-badge">
                      <i className="fas fa-star"></i> {t('blogs.featured')}
                    </span>
                  </div>
                  <div className="featured-content">
                    <div className="blog-meta">
                      <span className="category-tag">{blog.category}</span>
                      <span className="read-time">
                        <i className="far fa-clock"></i> {blog.readTime}
                      </span>
                    </div>
                    <h3>{blog.title}</h3>
                    <p>{blog.excerpt}</p>
                    <div className="blog-footer">
                      <span className="author">
                        <i className="fas fa-user-circle"></i> {blog.author}
                      </span>
                      <span className="date">
                        <i className="far fa-calendar"></i> {blog.date}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Blogs Section */}
        <div className="all-blogs-section">
          <h2 className="section-title">
            <i className="fas fa-newspaper"></i>
            {selectedCategory === blogCategories[0] ? t('blogs.allArticles') : selectedCategory}
            <span className="results-count">
              ({filteredBlogs.length} {t('blogs.articlesLower')})
            </span>
          </h2>

          {filteredBlogs.length === 0 ? (
            <div className="no-results">
              <i className="fas fa-search"></i>
              <h3>{t('blogs.noResults')}</h3>
              <p>{t('blogs.tryDifferent')}</p>
            </div>
          ) : (
            <div className="blog-grid">
              {filteredBlogs.map(blog => (
                <div
                  key={blog.id}
                  className="blog-card"
                  onClick={() => handleBlogClick(blog.slug)}
                >
                  <div className="blog-image">
                    <img src={blog.image} alt={blog.title} />
                    <span className="category-badge">{blog.category}</span>
                  </div>
                  <div className="blog-content">
                    <h3>{blog.title}</h3>
                    <p className="blog-excerpt">{blog.excerpt}</p>
                    <div className="blog-tags">
                      {blog.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="tag">#{tag}</span>
                      ))}
                    </div>
                    <div className="blog-footer">
                      <span className="read-time">
                        <i className="far fa-clock"></i> {blog.readTime}
                      </span>
                      <span className="date">{blog.date}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Call to Action */}
        <div className="blog-cta">
          <div className="cta-content">
            <i className="fas fa-vote-yea"></i>
            <h3>{t('blogs.cta.title')}</h3>
            <p>{t('blogs.cta.subtitle')}</p>
            <div className="cta-buttons">
              <button className="cta-btn primary" onClick={() => navigate('/voter-poll')}>
                <i className="fas fa-check-circle"></i>
                {t('blogs.cta.register')}
              </button>
              <button className="cta-btn secondary" onClick={() => navigate('/results')}>
                <i className="fas fa-chart-line"></i>
                {t('blogs.cta.viewResults')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogList;
